import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatARS } from "../../lib/site";
import { SeatGrid } from "./SeatGrid";

export const priceMap = (trip) => Object.fromEntries((trip.categories ?? [{ category: trip.category, price: trip.price }]).map((c) => [c.category, c.price]));

export const MAX_SEATS = 10;

export const SeatMap = ({ trip, selected, onChange, onCats }) => {
  const [data, setData] = useState(null);
  const prices = priceMap(trip);

  useEffect(() => {
    let alive = true;
    api.get(`/trips/${trip.id}/seatmap`, { params: { from_idx: trip.from_idx, to_idx: trip.to_idx } })
      .then((r) => {
        if (!alive) return;
        const decks = r.data?.decks ?? [];
        setData({ decks, occupied: r.data?.occupied ?? [] });
        onCats?.(Object.fromEntries(decks.flatMap((d) => (d.cells ?? []).filter((c) => c.kind === "seat").map((c) => [c.number, c.category]))));
      })
      .catch(() => { if (alive) setData({ decks: [], occupied: [] }); });
    return () => { alive = false; };
  }, [trip.id, trip.from_idx, trip.to_idx]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (n) => {
    if (selected.includes(n)) return onChange(selected.filter((s) => s !== n));
    if (selected.length >= MAX_SEATS) return toast.error(`Podés elegir hasta ${MAX_SEATS} butacas por tramo.`);
    return onChange([...selected, n]);
  };

  if (!data) return <div className="flex items-center gap-2 py-10 text-[var(--muted-ink)]"><Loader2 className="h-5 w-5 animate-spin" /> Cargando butacas…</div>;

  return (
    <div data-testid="seat-map">
      <div className="mb-4 flex flex-wrap gap-4 text-xs text-[var(--muted-ink)]">
        {Object.entries(prices).map(([c, p]) => <span key={c} data-testid={`seat-legend-${c}`} className="flex items-center gap-2"><span className="h-4 w-4 rounded border-2 border-[var(--forest)]/40 bg-white" /> {c} · {formatARS(p)}</span>)}
        <span className="flex items-center gap-2"><span className="h-4 w-4 rounded border-2 border-[var(--forest)] bg-[var(--lime)]" /> Tu elección</span>
        <span className="flex items-center gap-2"><span className="h-4 w-4 rounded bg-black/10" /> Ocupada</span>
      </div>
      <p className="mb-3 text-xs text-[var(--muted-ink)]">Tocá una butaca para elegirla y volvé a tocarla para soltarla. Podés elegir varias (hasta {MAX_SEATS}) y combinar categorías y plantas.</p>
      <SeatGrid
        decks={data.decks}
        onSeat={toggle}
        stateOf={(n, cell) => (prices[cell.category] == null ? "off" : data.occupied.includes(n) ? "taken" : selected.includes(n) ? "selected" : "free")}
      />
      <div className="mt-4 rounded-[22px] bg-[var(--ink)] p-5 text-white">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--lime)]">Butacas elegidas · {trip.from_stop} → {trip.to_stop}</p>
        <p data-testid="seat-selection-summary" className="mt-2 font-display text-3xl">{selected.length ? selected.join(" · ") : "—"}</p>
        <p data-testid="seat-selection-count" className="mt-1 text-sm text-white/75">{selected.length} butaca(s) · un pasajero por butaca</p>
      </div>
    </div>
  );
};
